import { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * HelpTooltip - In-app contextual help component
 * Shows a '?' icon that displays a tooltip or links to Help Centre
 * 
 * Usage:
 * <HelpTooltip 
 *   term="Scope 3"
 *   tooltip="Indirect emissions from activities like travel, waste, and value chain"
 *   articleId="scopes"
 *   categoryId="compliance"
 * />
 */
const HelpTooltip = ({
  term,
  tooltip,
  articleId = null,
  categoryId = null,
  size = 'sm' // 'sm' | 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5'
  };

  const handleLearnMore = () => {
    setIsOpen(false);
    if (articleId && categoryId) {
      // Navigate to specific article (would need state management or URL params)
      navigate('/help');
    } else {
      navigate('/help');
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ml-1 text-slate-400 hover:text-[#74A57F] transition-colors focus:outline-none"
        aria-label={`Help: ${term}`}
        data-testid={`help-tooltip-${term?.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <HelpCircle className={sizeClasses[size]} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 bg-black/20 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Tooltip */}
          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 md:bottom-auto md:top-full md:mt-2">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3 w-64 md:w-72">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-[#003B5C] text-sm">{term}</h4>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-slate-600 text-xs leading-relaxed mb-3">
                {tooltip}
              </p>
              <button
                onClick={handleLearnMore}
                className="text-[#74A57F] text-xs font-medium hover:underline"
              >
                Learn more in Help Centre →
              </button>
            </div>
            {/* Arrow */}
            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full md:bottom-auto md:top-0 md:-mt-2">
              <div className="border-8 border-transparent border-t-white md:border-t-transparent md:border-b-white" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Pre-defined help content for common terms
export const helpContent = {
  scope1: {
    term: 'Scope 1',
    tooltip: 'Direct emissions from sources you own or control, such as fuel combustion in vehicles or natural gas heating.'
  },
  scope2: {
    term: 'Scope 2',
    tooltip: 'Indirect emissions from purchased electricity. These vary by state based on the energy grid mix.'
  },
  scope3: {
    term: 'Scope 3',
    tooltip: 'Other indirect emissions from your value chain, including business travel, waste disposal, and water usage. Optional for most SMEs.'
  },
  gridIntensity: {
    term: 'Grid Intensity',
    tooltip: 'The amount of CO₂ emitted per kWh of electricity. Varies by state: VIC is highest (0.82), TAS is lowest (0.15).'
  },
  greenPower: {
    term: 'GreenPower',
    tooltip: 'Government-accredited renewable energy. When you purchase GreenPower, your electricity emissions are counted as zero.'
  },
  baselineYear: {
    term: 'Baseline Year',
    tooltip: 'The reference year against which you measure emission reductions. Choose a year with complete, reliable data.'
  },
  financialYear: {
    term: 'Financial Year',
    tooltip: 'Australian financial year runs July 1 to June 30. FY 2025/26 means July 2025 - June 2026.'
  },
  tco2e: {
    term: 'tCO₂-e',
    tooltip: 'Tonnes of carbon dioxide equivalent. A standard unit that includes CO₂ and other greenhouse gases converted to their CO₂ warming potential.'
  },
  ngaFactors: {
    term: 'NGA Factors',
    tooltip: 'National Greenhouse Accounts factors published annually by the Australian Government. Lucent Sky ESG applies the applicable NGA factors for each financial year automatically.'
  },
  evidenceVault: {
    term: 'Evidence Vault',
    tooltip: 'Secure storage for supporting documents (bills, receipts, certificates). Link evidence to specific entries for audit-ready compliance.'
  },
  // Data entry field help
  category: {
    term: 'Category',
    tooltip: 'Select the type of emission activity. Categories are grouped by Scope: Energy (Scope 1-2), Transport (Scope 3), and Waste (Scope 3).'
  },
  quantity: {
    term: 'Quantity',
    tooltip: 'Enter the amount consumed. Use your utility bill, fuel receipt, or travel records. The unit depends on the category selected.'
  },
  entryDate: {
    term: 'Entry Date',
    tooltip: 'The date of the activity or invoice. This determines which financial year the emission is counted in (July-June).'
  },
  notes: {
    term: 'Notes',
    tooltip: 'Add any relevant details like invoice number, supplier, or context. Helpful for audits and tracking.'
  },
  electricity: {
    term: 'Electricity',
    tooltip: 'Grid electricity usage in kWh. Find this on your electricity bill. Emission factor varies by state based on energy mix.'
  },
  naturalGas: {
    term: 'Natural Gas',
    tooltip: 'Gas usage in MJ (megajoules). Check your gas bill for consumption. This is a Scope 1 direct emission.'
  },
  fuel: {
    term: 'Vehicle Fuel',
    tooltip: 'Fuel for company vehicles in litres. Petrol, diesel, and LPG have different emission factors. This is a Scope 1 emission.'
  },
  flights: {
    term: 'Business Flights',
    tooltip: 'Air travel in km. Short (<1,500km), Medium (1,500-3,700km), Long (>3,700km). This is a Scope 3 emission.'
  },
  waste: {
    term: 'Waste',
    tooltip: 'Waste disposal in tonnes. Landfill has highest emissions. Recycling and composting have lower factors.'
  },
  reductionTarget: {
    term: 'Reduction Target',
    tooltip: 'Set a percentage reduction goal from your baseline year. Common targets: 30% by 2030, 50% by 2035, Net Zero by 2050.'
  }
};

export default HelpTooltip;
